export type ApiErrorCode =
  | "INVALID_REQUEST"
  | "ARTIFACT_INVALID"
  | "UNAUTHENTICATED"
  | "FORBIDDEN"
  | "ACCESS_PENDING"
  | "NOT_FOUND"
  | "RUN_ACTIVE"
  | "INVALID_TRANSITION"
  | "DUPLICATE_COMMAND"
  | "FIXED_ADMIN_IMMUTABLE"
  | "ENVIRONMENT_NOT_READY"
  | "PAYLOAD_TOO_LARGE"
  | "INTERNAL"
  | "RUNTIME_NOT_READY"
  | "ENGINE_UNAVAILABLE"
  | "IDENTITY_UNAVAILABLE"
  | "SECRET_STORE_UNAVAILABLE"
  | "SHARING_UNAVAILABLE"
  | "AI_NOT_CONFIGURED"
  | "AI_PROVIDER_ERROR"
  | "AI_TIMEOUT"
  | "AI_OUTPUT_INVALID"
  | "JIRA_NOT_CONFIGURED"
  | "JIRA_ERROR"
  | "JIRA_PAYLOAD_BLOCKED"
  | "OPERATION_IN_PROGRESS"
  | "NETWORK";

export type UiBehaviour =
  | "refresh-then-session-dialog"
  | "permission"
  | "waiting-screen"
  | "not-found"
  | "inline-fields"
  | "wizard-step-1"
  | "run-active"
  | "state-changed-refetch"
  | "treat-as-accepted"
  | "wizard-step-2"
  | "recovering"
  | "unhealthy"
  | "identity-unavailable"
  | "runtime-not-connected"
  | "secret-store-unavailable"
  | "ai-not-configured"
  | "jira-not-configured"
  | "generic";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly requestId: string;

  constructor(status: number, code: ApiErrorCode, message: string, requestId: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

const KNOWN = new Set<string>([
  "INVALID_REQUEST",
  "ARTIFACT_INVALID",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "ACCESS_PENDING",
  "NOT_FOUND",
  "RUN_ACTIVE",
  "INVALID_TRANSITION",
  "DUPLICATE_COMMAND",
  "FIXED_ADMIN_IMMUTABLE",
  "ENVIRONMENT_NOT_READY",
  "PAYLOAD_TOO_LARGE",
  "INTERNAL",
  "RUNTIME_NOT_READY",
  "ENGINE_UNAVAILABLE",
  "IDENTITY_UNAVAILABLE",
  "SECRET_STORE_UNAVAILABLE",
  "SHARING_UNAVAILABLE",
  "AI_NOT_CONFIGURED",
  "AI_PROVIDER_ERROR",
  "AI_TIMEOUT",
  "AI_OUTPUT_INVALID",
  "JIRA_NOT_CONFIGURED",
  "JIRA_ERROR",
  "JIRA_PAYLOAD_BLOCKED",
  "OPERATION_IN_PROGRESS",
]);

function codeForStatus(status: number): ApiErrorCode {
  if (status === 400) return "INVALID_REQUEST";
  if (status === 401) return "UNAUTHENTICATED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 413) return "PAYLOAD_TOO_LARGE";
  if (status === 503) return "ENGINE_UNAVAILABLE";
  return "INTERNAL";
}

function scrub(message: string): string {
  return message
    .replace(/bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [REDACTED]")
    .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[REDACTED]");
}

export async function toApiError(res: Response): Promise<ApiError> {
  const headerId = res.headers.get("x-request-id") ?? "";
  let body: unknown = null;
  try {
    body = await res.clone().json();
  } catch {
    body = null;
  }
  const env = (body as { error?: { code?: string; message?: string; request_id?: string } } | null)?.error;
  if (env && typeof env === "object") {
    const code = env.code && KNOWN.has(env.code) ? (env.code as ApiErrorCode) : codeForStatus(res.status);
    return new ApiError(res.status, code, scrub(env.message ?? "Request failed."), env.request_id ?? headerId);
  }
  return new ApiError(res.status, codeForStatus(res.status), `The server returned an unexpected response (HTTP ${res.status}).`, headerId);
}

export function networkError(cause: unknown): ApiError {
  const e = new ApiError(0, "NETWORK", "Couldn't reach the server.", "");
  (e as { cause?: unknown }).cause = cause;
  return e;
}

const BEHAVIOUR: Partial<Record<ApiErrorCode, UiBehaviour>> = {
  UNAUTHENTICATED: "refresh-then-session-dialog",
  FORBIDDEN: "permission",
  ACCESS_PENDING: "waiting-screen",
  NOT_FOUND: "not-found",
  INVALID_REQUEST: "inline-fields",
  ARTIFACT_INVALID: "wizard-step-1",
  RUN_ACTIVE: "run-active",
  INVALID_TRANSITION: "state-changed-refetch",
  DUPLICATE_COMMAND: "treat-as-accepted",
  ENVIRONMENT_NOT_READY: "wizard-step-2",
  RUNTIME_NOT_READY: "recovering",
  ENGINE_UNAVAILABLE: "unhealthy",
  IDENTITY_UNAVAILABLE: "identity-unavailable",
  SECRET_STORE_UNAVAILABLE: "secret-store-unavailable",
  AI_NOT_CONFIGURED: "ai-not-configured",
  JIRA_NOT_CONFIGURED: "jira-not-configured",
  NETWORK: "runtime-not-connected",
};

export function uiBehaviourFor(err: unknown): UiBehaviour {
  if (!isApiError(err)) return "generic";
  return BEHAVIOUR[err.code] ?? "generic";
}

export function plainMessage(err: unknown): string {
  if (isApiError(err)) return err.message;
  return "Something went wrong.";
}
